import React, { useEffect, useState, useCallback, useMemo } from "react";
import {useGetUserSettings} from "../hooks/useBackend";
import analyticsService from "../services/analyticsService";
import { clearOnboardingLocalState } from "../utils/onboardingStatus";
import { getUserIdFromAuthToken, getAuthTokenExpiryMs, isAuthTokenExpired } from "../utils/jwtPayload";
import { markSessionExpired } from "../utils/sessionExpiry";
import { stripServerLayoutSettings } from "../utils/layoutLocalStorage";
import { applyOverlayAppSettings } from "../utils/overlayAppSettings";
import {
    getAppSettings,
    hasLocalAppSettingsEdits,
    isAppSettingsStoreActive,
    resetAppSettingsStore,
    adoptServerAppSettings,
    seedAppSettingsFromServer,
    subscribeAppSettings,
} from "../utils/appSettingsStore";
import { queryClient } from "../lib/queryClient";


export const AuthContext = React.createContext({});

const ORIGINAL_AUTH_TOKEN_KEY = 'originalAuthToken';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** useApi returns `{}` when fetch fails (network/CORS) — not an auth error. */
const isBackendUnreachable = (settings) =>
    settings != null &&
    typeof settings === 'object' &&
    Object.keys(settings).length === 0;

const isInvalidSessionResponse = (settings) =>
    settings?.error === 'User not found';

const sanitizeUserAppSettings = (userData) => {
    if (!userData || userData.appSettings == null) return userData;
    return {
        ...userData,
        appSettings: stripServerLayoutSettings(applyOverlayAppSettings(userData.appSettings)),
    };
};

const clearUserScopedLocalState = () => {
    localStorage.removeItem('isPaperTrading');
    localStorage.removeItem('defaultAccount');
    localStorage.removeItem('defaultTicker');
    localStorage.removeItem('defaultSelectedTickerDetails');
};

const restoreOriginalAuthTokenIfStashed = () => {
    try {
        const original = sessionStorage.getItem(ORIGINAL_AUTH_TOKEN_KEY);
        if (!original) return false;
        sessionStorage.removeItem(ORIGINAL_AUTH_TOKEN_KEY);
        localStorage.setItem('authToken', original);
        return true;
    } catch (_) {
        return false;
    }
};

// Migration helper: Extract token from old user cache (runs once before component)
const migrateOldUserCache = () => {
    try {
        const oldUser = localStorage.getItem('user');
        if (oldUser && oldUser !== 'null') {
            const parsed = JSON.parse(oldUser);
            if (parsed?.token) {
                // Migrate token to new location
                localStorage.setItem('authToken', parsed.token);
                console.log('✅ Migrated token from old user cache');
            }
            // Remove old user object
            localStorage.removeItem('user');
        }
    } catch (e) {
        // If migration fails, just remove the old key
        localStorage.removeItem('user');
    }
};

// Run migration once when module loads
migrateOldUserCache();

export const AuthProvider = ({ children }) => {
    // Frontend no longer manages TradeStation credentials
    const refreshAccessToken = async () => null;
    const getStoredCredentials = async () => null;
    const { getUserSettings } = useGetUserSettings();
    
    // Only cache token, not full user object
    const cachedToken = localStorage.getItem('authToken');
    const [user, setUser] = useState(null);

    // Keep the settings store aligned with the signed-in user before any child
    // effect can save. After a local edit, a later settings fetch must not
    // replace that copy.
    if (!user) {
        if (isAppSettingsStoreActive()) resetAppSettingsStore();
    } else if (user.appSettings) {
        seedAppSettingsFromServer(user.appSettings);
    }
    const [isInitializing, setIsInitializing] = useState(!!cachedToken);
    // True when the backend is unreachable (network/CORS failure) — distinct from
    // an expired/invalid token. We preserve the session so the user can retry
    // without losing their login when Railway comes back up.
    const [backendDown, setBackendDown] = useState(false);

    const logout = useCallback(({ expired = false } = {}) => {
        if (expired) markSessionExpired();
        try {
            const uid = user?.id;
            if (uid != null) clearOnboardingLocalState(uid);
        } catch (_) { /* ignore */ }
        setUser(null);
        setBackendDown(false);
        localStorage.removeItem('authToken');
        try {
            sessionStorage.removeItem(ORIGINAL_AUTH_TOKEN_KEY);
        } catch (_) { /* ignore */ }
        // QueryClient is a module singleton and survives Environment remount.
        // Drop the previous user's cached queries so the next login cannot
        // refetch stale watchlists / tickers with the new token.
        queryClient.clear();
        // No TradeStation credentials stored on client anymore
        clearUserScopedLocalState();
        
        // Clear session tracking data to prevent source attribution to wrong user
        // This ensures that if someone else uses the same browser, they don't get
        // the previous user's source tracking (UTM params, registration source, etc.)
        try {
            sessionStorage.removeItem('registration_source');
            sessionStorage.removeItem('utm_params');
        } catch (e) {
            // sessionStorage not available (e.g., in private browsing)
        }
        
        // Reset analytics session to generate new session ID
        analyticsService.resetSession();
        
        console.log('✅ Logout completed - session tracking cleared');
    }, [user?.id]);

    // An expired impersonation JWT should drop the superuser back into their own
    // session rather than logging them out of the app entirely.
    const handleExpiredSession = useCallback(() => {
        if (restoreOriginalAuthTokenIfStashed()) {
            clearUserScopedLocalState();
            window.location.assign('/admin/users');
            return;
        }
        logout({ expired: true });
    }, [logout]);

    /**
     * Swap to a server-minted impersonation JWT (superuser support).
     * Stashes the real token in sessionStorage and remounts as the target user.
     */
    const startImpersonation = useCallback((impersonationToken) => {
        if (!impersonationToken || typeof impersonationToken !== 'string') {
            throw new Error('Missing impersonation token');
        }
        const current = user?.token || localStorage.getItem('authToken');
        if (!current) {
            throw new Error('No active session to stash');
        }
        try {
            sessionStorage.setItem(ORIGINAL_AUTH_TOKEN_KEY, current);
        } catch (e) {
            throw new Error('Unable to stash original session');
        }
        localStorage.setItem('authToken', impersonationToken);
        clearUserScopedLocalState();
        window.location.assign('/trade');
    }, [user?.token]);

    /**
     * Exit impersonation: audit on server, restore stashed superuser token, return to Admin.
     */
    const exitImpersonation = useCallback(async () => {
        const impersonationToken = user?.token || localStorage.getItem('authToken');
        if (impersonationToken && BACKEND_URL) {
            try {
                await fetch(`${BACKEND_URL}/admin/impersonate/exit`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${impersonationToken}`,
                    },
                });
            } catch (_) {
                // Best-effort audit; always restore local session
            }
        }

        const restored = restoreOriginalAuthTokenIfStashed();
        clearUserScopedLocalState();
        if (!restored) {
            localStorage.removeItem('authToken');
            window.location.assign('/login');
            return;
        }
        window.location.assign('/admin/users');
    }, [user?.token]);

    // Custom setUser that only persists the token and fetches full settings
    const setUserAndPersistToken = useCallback(async (userData) => {
        if (userData && userData.token) {
            // Store only the token in localStorage
            localStorage.setItem('authToken', userData.token);
            
            // Set initial user data immediately (for basic info like email, id)
            setUser(sanitizeUserAppSettings(userData));
            
            // Mark as initializing while we fetch full settings
            setIsInitializing(true);
            
            // Fetch and merge full user settings from backend
            try {
                const settings = await getUserSettings(userData.token);
                if (isBackendUnreachable(settings)) {
                    console.warn('[AuthProvider] Backend unreachable after login — showing outage UI');
                    setBackendDown(true);
                    return;
                }
                if (isInvalidSessionResponse(settings)) {
                    logout();
                    return;
                }
                if (settings?.email) {
                    setBackendDown(false);
                    // Merge login response data with full settings
                    let fullUserData = {
                        ...userData,
                        ...settings,
                        token: userData.token // Ensure token is preserved
                    };
                    if (fullUserData.id == null) {
                        const fromJwt = getUserIdFromAuthToken(userData.token);
                        if (fromJwt != null) {
                            fullUserData = { ...fullUserData, id: fromJwt };
                        }
                    }
                    setUser(sanitizeUserAppSettings(fullUserData));
                    console.log('✅ User data initialized with full settings');
                } else {
                    setBackendDown(false);
                    const fromJwt = getUserIdFromAuthToken(userData.token);
                    setUser(sanitizeUserAppSettings({
                        ...userData,
                        ...(fromJwt != null ? { id: fromJwt } : {}),
                        token: userData.token,
                    }));
                    console.warn('⚠️ Could not fetch user settings after login');
                }
            } catch (error) {
                console.error('Error fetching user settings after login:', error);
            } finally {
                setIsInitializing(false);
            }
        } else {
            // Clear everything on null/undefined
            localStorage.removeItem('authToken');
            setUser(null);
        }
    }, [getUserSettings]);

    // Update user settings in context (called from Settings page).
    // Stable identity — merge via functional setState so callers that list
    // updateUserSettings in effect deps do not re-fire on every user change.
    const updateUserSettings = useCallback((settings) => {
        setUser((prev) => {
            if (!prev) return prev;

            const updatedUser = {
                ...prev,
                id: settings.id ?? prev.id,
                email: settings.email ?? prev.email,
                tradeConfirmation: settings.tradeConfirmation ?? prev.tradeConfirmation,
                showTooltips: settings.showTooltips ?? prev.showTooltips,
                superuser: settings.superuser ?? prev.superuser,
                beta_user: settings.beta_user ?? prev.beta_user,
                is_demo: settings.is_demo ?? prev.is_demo,
                early_access: settings.early_access ?? prev.early_access,
                early_access_started_at: settings.early_access_started_at ?? prev.early_access_started_at,
                referral_code: settings.referral_code ?? prev.referral_code,
                createdAt: settings.createdAt ?? prev.createdAt,
                // Use `!== undefined` so a true→false transition (shouldn't happen, but be safe)
                // or an explicit skip flag actually propagates instead of getting stuck on the
                // stale value captured at login — this previously caused onboarding to bounce
                // back to the phone-verify step after refreshUserSettings() calls later in the flow.
                phoneNumber: settings.phoneNumber !== undefined ? settings.phoneNumber : prev.phoneNumber,
                phoneVerified: settings.phoneVerified !== undefined ? settings.phoneVerified : prev.phoneVerified,
                phoneVerificationSkipped: settings.phoneVerificationSkipped !== undefined ? settings.phoneVerificationSkipped : prev.phoneVerificationSkipped,
                phoneVerificationConfigured: settings.phoneVerificationConfigured !== undefined ? settings.phoneVerificationConfigured : prev.phoneVerificationConfigured,
                firstName: settings.firstName !== undefined ? settings.firstName : prev.firstName,
                // Use `!== undefined` (not `??`) so an explicit null/false from the server or a
                // disconnect can actually clear these. With `??`, passing activeBrokerConnection: null
                // would preserve the stale connection, leaving the broker guard tripped until reload.
                hasTradeStationCredentials: (settings.hasTradeStationCredentials !== undefined ? settings.hasTradeStationCredentials : prev.hasTradeStationCredentials),
                hasBrokerConnection: (settings.hasBrokerConnection !== undefined ? settings.hasBrokerConnection : prev.hasBrokerConnection),
                brokerConnections: (settings.brokerConnections !== undefined ? settings.brokerConnections : prev.brokerConnections),
                activeBrokerConnection: (settings.activeBrokerConnection !== undefined ? settings.activeBrokerConnection : prev.activeBrokerConnection),
                subscriptionStatus: settings.subscriptionStatus ?? prev.subscriptionStatus,
                appSettings: (settings.appSettings !== undefined
                    ? stripServerLayoutSettings(hasLocalAppSettingsEdits()
                        ? getAppSettings()
                        : adoptServerAppSettings(settings.appSettings))
                    : prev.appSettings),
                accountSettings: (settings.accountSettings !== undefined ? settings.accountSettings : prev.accountSettings),
                accountDefaults: (settings.accountSettings !== undefined ? settings.accountSettings : prev.accountSettings || prev.accountDefaults), // Legacy support
                maintenanceStatus: (settings.maintenanceStatus !== undefined ? settings.maintenanceStatus : prev.maintenanceStatus),
                tickerOptions: (settings.tickerOptions !== undefined ? settings.tickerOptions : prev.tickerOptions),
                legalAcceptance: (settings.legalAcceptance !== undefined ? settings.legalAcceptance : prev.legalAcceptance),
                impersonating: settings.impersonating !== undefined ? settings.impersonating : prev.impersonating,
                actor: settings.actor !== undefined ? settings.actor : prev.actor,
            };

            try {
                if (JSON.stringify(updatedUser) === JSON.stringify(prev)) return prev;
            } catch (_) {}

            return updatedUser;
        });
    }, []);

    subscribeAppSettings((next) => {
        updateUserSettings({ appSettings: next });
    });
    
    // Re-check backend reachability (e.g. outage card "Try Again" without full reload)
    const retryBackendConnection = useCallback(async () => {
        const token = user?.token || localStorage.getItem('authToken');
        if (!token) {
            setBackendDown(false);
            return false;
        }
        try {
            const settings = await getUserSettings(token);
            if (isBackendUnreachable(settings)) {
                setBackendDown(true);
                return false;
            }
            if (isInvalidSessionResponse(settings)) {
                logout();
                return false;
            }
            if (settings?.email) {
                setBackendDown(false);
                if (user?.token) {
                    updateUserSettings(settings);
                } else {
                    let userData = { token, ...settings };
                    if (userData.id == null) {
                        const fromJwt = getUserIdFromAuthToken(token);
                        if (fromJwt != null) userData = { ...userData, id: fromJwt };
                    }
                    setUser(sanitizeUserAppSettings(userData));
                }
                return true;
            }
            setBackendDown(false);
            return false;
        } catch (error) {
            console.error('[AuthProvider] retryBackendConnection failed:', error);
            return false;
        }
    }, [user?.token, getUserSettings, updateUserSettings]);

    // Refresh user settings from server
    const refreshUserSettings = useCallback(async () => {
        if (!user?.token) {
            return;
        }

        const settings = await getUserSettings(user.token);
        if (isBackendUnreachable(settings)) {
            setBackendDown(true);
            return;
        }
        if (isInvalidSessionResponse(settings)) {
            logout();
            return;
        }
        if (settings?.email) {
            setBackendDown(false);
            updateUserSettings(settings);
            return settings;
        }
    }, [user?.token, getUserSettings, updateUserSettings, logout]);

    const refresh = useCallback(async () => null, []);

    const fetchStoredCredentials = useCallback(async () => null, []);

    // Initialize user from cached token on app startup
    useEffect(() => {
        if (!cachedToken) {
            setIsInitializing(false);
            return;
        }

        // Expired JWT: the backend would answer 403 with a plain-text body, which
        // reads as a generic error here and produced a silent logout. Detect it up
        // front so the login screen can say the session expired.
        if (isAuthTokenExpired(cachedToken)) {
            console.warn('[AuthProvider] Cached token expired — signing out');
            handleExpiredSession();
            setIsInitializing(false);
            return;
        }

        const initializeUserFromToken = async () => {
            try {
                const settings = await getUserSettings(cachedToken);

                // useApi.js returns {} (empty object, zero keys) when the fetch itself
                // fails (network error, CORS block, Railway edge down). This is NOT an
                // auth failure — the token is still valid. Preserve the session and
                // surface the outage UI instead of logging the user out.
                if (isBackendUnreachable(settings)) {
                    console.warn('[AuthProvider] Backend unreachable — preserving session, showing outage UI');
                    setBackendDown(true);
                    return;
                }

                if (isInvalidSessionResponse(settings)) {
                    console.warn('[AuthProvider] Cached token references deleted user — logging out');
                    if (restoreOriginalAuthTokenIfStashed()) {
                        clearUserScopedLocalState();
                        window.location.assign('/admin/users');
                        return;
                    }
                    logout();
                    return;
                }

                // Successful settings fetch — user has a valid token and backend responded
                if (settings && settings.email) {
                    setBackendDown(false);
                    let userData = {
                        token: cachedToken,
                        ...settings
                    };
                    if (userData.id == null) {
                        const fromJwt = getUserIdFromAuthToken(cachedToken);
                        if (fromJwt != null) {
                            userData = { ...userData, id: fromJwt };
                        }
                    }
                    setUser(sanitizeUserAppSettings(userData));
                    
                    // Initialize analytics and identify user in PostHog for auto-login
                    analyticsService.init(settings.id, cachedToken);
                    analyticsService.identifyUser(settings.id, {
                        email: settings.email,
                        subscription_status: settings.subscriptionStatus || 'unknown',
                        has_tradestation: settings.hasTradeStationCredentials || false,
                        early_access: settings.early_access || false,
                        beta_user: settings.beta_user || false,
                    });
                } else {
                    // Server responded but token is invalid/expired — restore stash or logout
                    if (restoreOriginalAuthTokenIfStashed()) {
                        clearUserScopedLocalState();
                        window.location.assign('/admin/users');
                        return;
                    }
                    logout();
                }
            } catch (error) {
                console.error('[AuthProvider] Error initializing user from token:', error);
                logout();
            } finally {
                setIsInitializing(false);
            }
        };

        initializeUserFromToken();
    }, []); // Only run once on mount

    // Sign out the moment the session JWT expires. Without this every request
    // just starts failing with 403 while the UI still looks logged in, and the
    // user only lands on /login after a manual reload.
    useEffect(() => {
        const token = user?.token;
        if (!token) return undefined;

        const expiryMs = getAuthTokenExpiryMs(token);
        if (expiryMs == null) return undefined; // legacy token without `exp`

        const signOutIfExpired = () => {
            if (isAuthTokenExpired(token)) handleExpiredSession();
        };

        if (isAuthTokenExpired(token)) {
            handleExpiredSession();
            return undefined;
        }

        // setTimeout caps out near 2^31ms (~24.9 days); fall back to the focus
        // and interval checks for longer-lived tokens.
        const msUntilExpiry = expiryMs - Date.now();
        const timer = msUntilExpiry <= 2147483647
            ? setTimeout(signOutIfExpired, msUntilExpiry + 1000)
            : null;
        const interval = setInterval(signOutIfExpired, 60_000);
        window.addEventListener('focus', signOutIfExpired);

        return () => {
            if (timer) clearTimeout(timer);
            clearInterval(interval);
            window.removeEventListener('focus', signOutIfExpired);
        };
    }, [user?.token, handleExpiredSession]);

    // Refresh settings when a no-card local trial reaches trialEnd (revoke access + show paywall)
    useEffect(() => {
        if (!user?.token) return undefined;
        const sub = user.subscriptionStatus?.subscription;
        if (!sub || sub.status !== 'trialing' || sub.hasStripeBilling || !sub.trialEnd) {
            return undefined;
        }

        const trialEndMs = new Date(sub.trialEnd).getTime();
        if (Number.isNaN(trialEndMs)) return undefined;

        const refreshIfExpired = () => {
            if (Date.now() >= trialEndMs) {
                refreshUserSettings();
            }
        };

        refreshIfExpired();

        const msUntil = trialEndMs - Date.now();
        const timer = msUntil > 0 ? setTimeout(refreshIfExpired, msUntil + 500) : null;
        const onFocus = () => refreshIfExpired();
        window.addEventListener('focus', onFocus);
        const interval = setInterval(refreshIfExpired, 60_000);

        return () => {
            if (timer) clearTimeout(timer);
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [
        user?.token,
        user?.subscriptionStatus?.subscription?.trialEnd,
        user?.subscriptionStatus?.subscription?.status,
        user?.subscriptionStatus?.subscription?.hasStripeBilling,
    ]);

    const values = useMemo(() => ({
        user,
        setUser: setUserAndPersistToken,
        refreshAccessToken: refresh,
        updateUserSettings,
        refreshUserSettings,
        logout,
        startImpersonation,
        exitImpersonation,
        isInitializing,
        backendDown,
        retryBackendConnection,
    }), [
        user,
        setUserAndPersistToken,
        refresh,
        updateUserSettings,
        refreshUserSettings,
        logout,
        startImpersonation,
        exitImpersonation,
        isInitializing,
        backendDown,
        retryBackendConnection,
    ]);

    return (
      <AuthContext.Provider value={values}>
          {children}
      </AuthContext.Provider>
    );
};